import { LayoutGrid, Loader2, Layers, Mail, MailOpen, Reply, Tag as TagIcon } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { SlimSidebar } from '../layout/SlimSidebar';
import { useTagKpi, type TagKpiTag } from '../../hooks/useTagKpi';
import { useFeed } from '../../hooks/useFeed';
import { useToReply } from '../../hooks/useToReply';
import { useUiStore } from '../../store/ui';
import { useMailStore } from '../../store/mail';
import { HelpHint } from '../ui/HelpHint';

/**
 * Vista Cruscotto: KPI dei tag/categorie spostati qui dalla vecchia vista "KPI Tag".
 * - Card riepilogo: totale messaggi, non letti e da rispondere (stessi dati di ListView).
 * - Una card CLICCABILE per ogni categoria AI (conteggio + data ultima mail).
 * - Riga di chip per i tag principali (colore + conteggio).
 * Il click su card/chip imposta pendingMailFilter nello store UI e naviga alla
 * vista Posta (ListView), che al mount applica il filtro ai suoi chip e lo azzera.
 */
export function DashboardView() {
  const { data, isLoading, isError } = useTagKpi();
  // Stessi dataset usati da ListView per le sue card KPI (31 giorni + da rispondere).
  const { data: feedItems } = useFeed(31);
  const { data: toReply } = useToReply();

  const setCurrentView = useUiStore((s) => s.setCurrentView);
  const setPendingMailFilter = useUiStore((s) => s.setPendingMailFilter);
  const setFeedFilter = useMailStore((s) => s.setFeedFilter);

  const categories = data?.categories ?? [];
  const tags = (data?.tags ?? []).slice(0, 30);
  const total = data?.total_messages ?? 0;
  const unseen = (feedItems ?? []).filter((it) => !it.is_seen).length;
  const toReplyCount = toReply?.length ?? 0;

  // Naviga alla Posta con un filtro "in attesa" (categoria o tag).
  const goToMail = (filter: { kind: 'category' | 'tag' | 'spam'; value: string } | null) => {
    setPendingMailFilter(filter);
    if (!filter) setFeedFilter({ kind: 'none' });
    setCurrentView('list');
  };
  // "Da rispondere" usa direttamente il filtro globale del feed (già condiviso con ListView).
  const goToReply = () => {
    setPendingMailFilter(null);
    setFeedFilter({ kind: 'toReply' });
    setCurrentView('list');
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      <SlimSidebar />

      <section className="flex flex-1 flex-col bg-zinc-950">
        {/* Header */}
        <div className="flex h-10 items-center gap-2 border-b border-zinc-800/80 px-4 text-xs">
          <LayoutGrid size={12} className="text-accent-400" />
          <span className="font-medium text-zinc-200">Cruscotto</span>
          {/* Aiuto contestuale della sezione */}
          <HelpHint topic="dashboard" />
          <span className="text-zinc-600">·</span>
          <span className="text-zinc-500">{total.toLocaleString('it-IT')} messaggi totali</span>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={16} className="animate-spin text-zinc-600" />
            </div>
          ) : isError ? (
            <div className="py-16 text-center text-sm text-zinc-500">
              Impossibile caricare il cruscotto. Riprova più tardi.
            </div>
          ) : (
            <div className="space-y-6">
              {/* Card riepilogo (stessi KPI della vista Posta) */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <SummaryCard
                  icon={<Mail size={16} />}
                  label="Totale messaggi"
                  value={total}
                  onClick={() => goToMail(null)}
                />
                <SummaryCard
                  icon={<MailOpen size={16} />}
                  label="Non letti (31 gg)"
                  value={unseen}
                  tone="warn"
                  onClick={() => goToMail(null)}
                />
                <SummaryCard
                  icon={<Reply size={16} />}
                  label="Da rispondere"
                  value={toReplyCount}
                  tone="ko"
                  onClick={goToReply}
                />
              </div>

              {/* Card per categoria */}
              {categories.length > 0 && (
                <div>
                  <div className="mb-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    <Layers size={11} /> Categorie
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                    {categories.map((c) => (
                      <button
                        key={c.category}
                        onClick={() => goToMail({ kind: 'category', value: c.category })}
                        title={`Apri la Posta filtrata su "${c.category || '(senza categoria)'}"`}
                        className="wm-dash-card group flex flex-col gap-1 p-4 text-left"
                      >
                        <span className="truncate text-[10px] font-semibold uppercase tracking-wider text-zinc-500 group-hover:text-accent-400">
                          {c.category || '(senza categoria)'}
                        </span>
                        <span className="text-3xl font-semibold tabular-nums text-zinc-100">
                          {c.count.toLocaleString('it-IT')}
                        </span>
                        {c.last_at && (
                          <span className="text-[11px] text-zinc-500">
                            ultima: {format(new Date(c.last_at), 'dd MMM yyyy HH:mm', { locale: it })}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Chip dei tag principali */}
              {tags.length > 0 && (
                <div>
                  <div className="mb-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                    <TagIcon size={11} /> Tag principali (top {tags.length})
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((t) => (
                      <TagChip key={t.id} tag={t} onClick={() => goToMail({ kind: 'tag', value: t.id })} />
                    ))}
                  </div>
                </div>
              )}

              {categories.length === 0 && tags.length === 0 && (
                <div className="py-16 text-center text-sm text-zinc-500">
                  Nessun dato disponibile: categorie e tag vengono creati automaticamente dall'AI.
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// Card riepilogo cliccabile (totale / non letti / da rispondere)
function SummaryCard({
  icon,
  label,
  value,
  tone,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone?: 'warn' | 'ko';
  onClick?: () => void;
}) {
  const valueCls =
    tone === 'ko' ? 'text-rose-400' : tone === 'warn' ? 'text-amber-400' : 'text-zinc-100';
  return (
    <button onClick={onClick} className="wm-dash-card flex items-center gap-3 p-4 text-left">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-500/15 text-accent-400">
        {icon}
      </div>
      <div>
        <div className={'text-[22px] font-bold leading-none tabular-nums ' + valueCls}>
          {value.toLocaleString('it-IT')}
        </div>
        <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-zinc-500">{label}</div>
      </div>
    </button>
  );
}

// Chip tag cliccabile: pallino colore + nome + conteggio
function TagChip({ tag, onClick }: { tag: TagKpiTag; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={`Apri la Posta filtrata sul tag "${tag.name}"`}
      className="wm-dash-chip flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium"
    >
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
      <span className="max-w-[140px] truncate">{tag.name}</span>
      <span className="tabular-nums text-zinc-500">{tag.count}</span>
    </button>
  );
}
